import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  Developer,
  DeveloperBulkUpdate,
  DeveloperBulkUpdateResource,
  DeveloperBulkUpdateSchema,
  Student,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { DeveloperUpdateSchema } from "./DeveloperUpdateSchema";
import { SelectedDevelopers } from "./SelectedDevelopers";

const routeName = "developerBulkUpdate";

export const developerBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Developers",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type DeveloperBulkUpdatePageState = {
  selectedIds: number[];
};

export const DeveloperBulkUpdatePage = () => {
  const listContext = useContext<ListContextValue>(ListContext);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [stateParam] = useState<DeveloperBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<DeveloperBulkUpdate>({});
  const [resource, setResource] = useState<DeveloperBulkUpdateResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);

    ApiService.developers.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.developers.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      resetUnsavedChanges([response]);
    });
  };

  const save = (bulkUpdate: DeveloperBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.developers
      .saveBulkUpdate(bulkUpdate)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
          dispatch(setAppToast(newToast("success")));
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{developerBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate.developers && bulkUpdate.developers?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="lg">
        <DeveloperUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as DeveloperBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<DeveloperBulkUpdate>("updateSchema")] || []}
        />

        <SelectedDevelopers
          developers={new Map(bulkUpdate.developers?.map((x) => [x.id, x]))}
          setDevelopers={(value) => setBulkUpdate({ ...bulkUpdate, developers: Array.from(value.values()) })}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
